import { useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { MoneyInput } from "@/components/ui/money-input";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { FluxoCaixa } from "@/hooks/useFluxoCaixa";
import { todayLocalISO } from "@/lib/date-utils";
import { AlertTriangle } from "lucide-react";
import { calculatePaymentFee, isCardPayment } from "@/lib/paymentFees";

interface FluxoCaixaFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: Omit<FluxoCaixa, "id" | "user_id" | "created_at" | "updated_at">) => Promise<void>;
  movimentacao?: FluxoCaixa | null;
}

const categoriasEntrada = [
  "Consulta",
  "Tratamento",
  "Ortodontia",
  "Implante",
  "Prótese",
  "Outros Recebimentos"
];

const categoriasSaida = [
  "Aluguel",
  "Água",
  "Luz",
  "Internet",
  "Telefone",
  "Material Odontológico",
  "Laboratório",
  "Salários",
  "Impostos",
  "Manutenção",
  "Limpeza",
  "Marketing",
  "Outras Despesas"
];

const formasPagamento = [
  "Dinheiro",
  "PIX",
  "Cartão de Crédito",
  "Cartão de Débito",
  "Boleto",
  "Transferência"
];

const newSubmissionKey = () => globalThis.crypto?.randomUUID?.()
  || `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export function FluxoCaixaForm({ open, onOpenChange, onSubmit, movimentacao }: FluxoCaixaFormProps) {
  const [formData, setFormData] = useState({
    tipo: "Entrada" as "Entrada" | "Saída",
    descricao: "",
    categoria: "",
    valor: 0,
    data_movimentacao: todayLocalISO(),
    forma_pagamento: "",
    taxa_operadora_percentual: 0,
    observacoes: "",
    subtipo: "movimentacao" as "movimentacao" | "sangria",
  });
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);
  const submissionKeyRef = useRef(newSubmissionKey());

  useEffect(() => {
    submissionKeyRef.current = newSubmissionKey();
    submittingRef.current = false;
    setSubmitting(false);
    if (movimentacao) {
      setFormData({
        tipo: movimentacao.tipo,
        descricao: movimentacao.descricao,
        categoria: movimentacao.categoria,
        valor: movimentacao.valor,
        data_movimentacao: String(movimentacao.data_movimentacao || "").split("T")[0],
        forma_pagamento: movimentacao.forma_pagamento || "",
        taxa_operadora_percentual: Number(movimentacao.taxa_operadora_percentual || 0),
        observacoes: movimentacao.observacoes || "",
        subtipo: movimentacao.subtipo === "sangria" || movimentacao.categoria === "Sangria" ? "sangria" : "movimentacao",
      });
    } else {
      setFormData({
        tipo: "Entrada",
        descricao: "",
        categoria: "",
        valor: 0,
        data_movimentacao: todayLocalISO(),
        forma_pagamento: "",
        taxa_operadora_percentual: 0,
        observacoes: "",
        subtipo: "movimentacao",
      });
    }
  }, [movimentacao, open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submittingRef.current) return;

    const isSangria = formData.tipo === "Saída" && formData.subtipo === "sangria";
    if (isSangria && !formData.observacoes.trim()) return;
    const valor = formData.valor;
    if (valor <= 0) return;
    const fee = calculatePaymentFee(valor, formData.forma_pagamento, formData.taxa_operadora_percentual);

    const submitData = {
      ...formData,
      valor,
      idempotency_key: submissionKeyRef.current,
      ...(formData.tipo === "Entrada" ? fee : calculatePaymentFee(valor, "", 0)),
      categoria: isSangria ? "Sangria" : formData.categoria,
      descricao: isSangria ? (formData.descricao.trim() || "Sangria de caixa") : formData.descricao,
      forma_pagamento: formData.forma_pagamento || undefined,
      observacoes: formData.observacoes || undefined,
      motivo: isSangria ? formData.observacoes.trim() : undefined,
    };

    submittingRef.current = true;
    setSubmitting(true);
    try {
      await onSubmit(submitData);
      onOpenChange(false);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const categorias = formData.tipo === "Entrada" ? categoriasEntrada : categoriasSaida;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[620px]">
        <DialogHeader>
          <DialogTitle>
            {movimentacao ? "Editar Movimentação" : "Nova Movimentação"}
          </DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="tipo">Tipo *</Label>
              <Select value={formData.tipo} onValueChange={(value: "Entrada" | "Saída") => setFormData({ ...formData, tipo: value, categoria: "", subtipo: "movimentacao" })} required>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Entrada">Entrada</SelectItem>
                  <SelectItem value="Saída">Saída</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {formData.tipo === "Saída" && (
              <div className="space-y-2">
                <Label>Natureza da saída *</Label>
                <Select
                  value={formData.subtipo}
                  onValueChange={(value: "movimentacao" | "sangria") => setFormData({
                    ...formData,
                    subtipo: value,
                    categoria: value === "sangria" ? "Sangria" : "",
                    descricao: value === "sangria" && !formData.descricao ? "Sangria de caixa" : formData.descricao,
                  })}
                >
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="movimentacao">Despesa</SelectItem>
                    <SelectItem value="sangria">Sangria</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className={formData.tipo === "Saída" ? "space-y-2 sm:col-span-2" : "space-y-2"}>
              <Label htmlFor="categoria">Categoria *</Label>
              <Select value={formData.categoria} onValueChange={(value) => setFormData({ ...formData, categoria: value })} required disabled={formData.subtipo === "sangria"}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione a categoria" />
                </SelectTrigger>
                <SelectContent>
                  {categorias.map((cat) => (
                    <SelectItem key={cat} value={cat}>
                      {cat}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.tipo === "Saída" && formData.subtipo === "sangria" && (
              <Alert variant="destructive" className="sm:col-span-2">
                <AlertTriangle className="h-4 w-4" />
                <AlertTitle>Esta operação reduz o saldo do caixa</AlertTitle>
                <AlertDescription>A sangria ficará registrada com data, unidade e usuário responsável.</AlertDescription>
              </Alert>
            )}

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="descricao">Descrição *</Label>
              <Input
                id="descricao"
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                placeholder="Ex: Recebimento de consulta"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="valor">Valor (R$) *</Label>
              <MoneyInput
                id="valor"
                autoComplete="off"
                value={formData.valor}
                onValueChange={(valor) => setFormData({ ...formData, valor })}
                placeholder="Ex.: 1.000,00"
                required
              />
            </div>

            <div className="space-y-2">
              <Label>Data *</Label>
              <Input
                type="date"
                value={formData.data_movimentacao}
                onChange={(event) => setFormData({ ...formData, data_movimentacao: event.target.value })}
                required
              />
            </div>

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="forma_pagamento">Forma de Pagamento</Label>
              <Select value={formData.forma_pagamento} onValueChange={(value) => setFormData({ ...formData, forma_pagamento: value })}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione" />
                </SelectTrigger>
                <SelectContent>
                  {formasPagamento.map((forma) => (
                    <SelectItem key={forma} value={forma}>
                      {forma}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.tipo === "Entrada" && isCardPayment(formData.forma_pagamento) && (
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="taxa_operadora_percentual">Taxa da operadora (%)</Label>
                <Input
                  id="taxa_operadora_percentual"
                  type="number"
                  min="0"
                  max="100"
                  step="0.01"
                  value={formData.taxa_operadora_percentual}
                  onChange={(event) => setFormData({ ...formData, taxa_operadora_percentual: Number(event.target.value) || 0 })}
                />
                <p className="text-xs text-muted-foreground">
                  Taxa {calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_taxa_operadora.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })} · Entrada líquida {calculatePaymentFee(formData.valor, formData.forma_pagamento, formData.taxa_operadora_percentual).valor_liquido.toLocaleString("pt-BR", { style: "currency", currency: "BRL" })}
                </p>
              </div>
            )}

            <div className="space-y-2 sm:col-span-2">
              <Label htmlFor="observacoes">{formData.subtipo === "sangria" ? "Motivo da sangria *" : "Observações"}</Label>
              <Textarea
                id="observacoes"
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={2}
                required={formData.subtipo === "sangria"}
              />
            </div>
          </div>

          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancelar
            </Button>
            <Button type="submit" disabled={submitting}>
              {movimentacao ? "Atualizar" : "Cadastrar"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
